import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from './api';

// Create Auth Context
const AuthContext = createContext();

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if user is stored in localStorage on app start
  useEffect(() => {
    const storedUser = localStorage.getItem('vienna_user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error('Error parsing stored user:', error);
        localStorage.removeItem('vienna_user');
      }
    }
    setLoading(false);
  }, []);

  // Login function
  const login = async (credentials) => {
    try {
      const response = await apiService.auth.login(credentials);
      
      if (response.data.status === 'success') {
        const userData = response.data.user;
        setUser(userData);
        localStorage.setItem('vienna_user', JSON.stringify(userData));
        return { success: true, user: userData };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      let message = error.response?.data?.message || 'Login failed';
      
      // Handle network/CORS errors
      if (error.message?.includes('Network Error') || !error.response) {
        message = 'Cannot connect to server. Please check if the backend is running.';
      }
      
      return { success: false, message };
    }
  };

  // Register function
  const register = async (userData) => {
    try {
      const response = await apiService.auth.register(userData);
      
      if (response.data.status === 'success') {
        return { success: true, message: 'Registration successful' };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Registration failed';
      return { success: false, message };
    }
  };

  // Logout function
  const logout = () => {
    setUser(null);
    localStorage.removeItem('vienna_user');
  };

  // Check if user is authenticated
  const isAuthenticated = () => {
    return user !== null;
  };

  const value = {
    user,
    login,
    register,
    logout,
    isAuthenticated,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 